import { Head, Link, usePage } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusBadge } from "@/components/status-badges"
import { ButtonLink } from "@/components/ui/button-link"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Progress } from "@/components/ui/progress"
import UpdateTimeline from "@/components/update-timeline"
import { CopyButton } from "@/components/copy-button"
import NotesDisplay from "@/components/notes-display"
import { VerticalTable } from "@/components/vertical-table"
import { ArrowLeft, Edit, Building2, Phone, Mail, MapPin, Plus, FileText, Users } from "lucide-react"
import { format, parseISO } from 'date-fns'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import type { Deal, Term, DealUpdate, User, Award } from '@/types/serializers'
import { DealProgress } from "@/components/deal-progress"


interface Props {
  current_user: User
  deal: Deal
  terms: Term[]
  deal_updates: DealUpdate[],
  awards: Award[],
  team_members: User[]
  [key: string]: any
}

export default function Show() {
  const { deal, terms, awards, team_members, current_user } = usePage<Props>().props

  if (!deal) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    )
  }


  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return format(parseISO(dateString), 'PPP');
    } catch (e) {
      return dateString;
    }
  };

  const formatPhoneNumber = (phoneNumber: string | undefined): string => {
    if (!phoneNumber) return '';

    try {
      const parsed = parsePhoneNumberFromString(phoneNumber, 'US')
      if (parsed) {
        return parsed.country === 'US'
          ? parsed.formatNational()
          : parsed.formatInternational()
      }
    } catch (e) {
      return phoneNumber
    }

    return phoneNumber
  }

  return (
    <>
      <Head title={`Deal: ${deal.name}`} />
      <div className="">
        {/* Header with navigation and actions */}
        <div className="flex items-center justify-between border-b border-b-gray-100 p-4 mb-0">
          <div className="flex items-center space-x-4">
            <Link href="/deals">
              <Button variant="outline" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {deal.name}
              </h1>
              <div className="text-md">
                <Link href={`/buyers/${deal.buyer?.id}`} className="hover:underline">
                  {deal.buyer?.name || "N/A"}
                </Link>
              </div>
            </div>
            <StatusBadge status={{type: 'deal', status: deal.status}} className="-mt-5 text-sm" />
          </div>
          <div className="flex items-center space-x-2">
            <ButtonLink
              href={`/deals/${deal.id}/edit`}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <Edit className="h-4 w-4" />
              <span>Edit</span>
            </ButtonLink>
          </div>
        </div>

        {/* Main content */}
        <div className="">
          <div className="grid grid-cols-1 md:grid-cols-3 min-h-screen">
            {/* Main content area */}
            <div className="md:col-span-2">
              {/* Key details section */}
              <div className="grid grid-cols-2 lg:grid-cols-5 border-b border-b-gray-100 p-6">
                <div className="flex items-start space-x-3">
                  <div>
                    <dt className="text-muted-foreground">Start Date</dt>
                    <dd className="font-medium">{formatDate(deal.start_date)}</dd>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div>
                    <dt className="text-muted-foreground">End Date</dt>
                    <dd className="font-medium">{formatDate(deal.end_date)}</dd>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-full">
                    <dt className="text-muted-foreground">Remaining</dt>
                    <dd className="font-medium mb-1">
                      <DealProgress
                        numerator={deal.months_remaining}
                        denominator={deal.months_total}
                        label="months"
                        variant="remaining"
                        labelPosition="above"
                        labelClassName="font-medium mb-1"
                      />
                    </dd>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-full">
                    <dt className="text-muted-foreground">Extensions</dt>
                    <dd className="font-medium mb-1">{deal.renewals_taken} / {deal.renewals} taken</dd>
                    <Progress
                      value={(deal.renewals_taken / deal.renewals) * 100}
                      className={`h-1.5  max-w-[100px] ${
                        deal.renewals_taken === deal.renewals
                          ? 'bg-red-100 [&>div]:bg-red-500'
                          : deal.renewals - deal.renewals_taken === 1
                            ? 'bg-yellow-100 [&>div]:bg-yellow-500'
                            : 'bg-green-100 [&>div]:bg-green-500'
                      }`}
                    />
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div>
                    <dt className="text-muted-foreground">Expiry Policy</dt>
                    <dd><StatusBadge status={{type: 'expiryPolicy', status: deal.expiry_policy}} className="mt-1 text-sm" /></dd>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Terms section */}
                <div>
                  <h2 className="text-lg font-semibold mb-4">Deal Terms</h2>
                  <div className="space-y-4">
                    <div className="overflow-x-auto border rounded-lg border-gray-100">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="text-xs text-muted-foreground border-b bg-gray-50 uppercase tracking-wider">
                            <th className="px-4 py-2 text-left font-medium">Term</th>
                            <th className="px-4 py-2 text-left font-medium">Status</th>
                            <th className="px-4 py-2 text-left font-medium">Length</th>
                            <th className="px-4 py-2 text-left font-medium">Start Date</th>
                            <th className="px-4 py-2 text-left font-medium">End Date</th>
                            <th className="px-4 py-2 text-left font-medium">SKU</th>
                          </tr>
                        </thead>
                        <tbody>
                          {terms && terms.length > 0 ? (
                            terms.map((term, index) => (
                              <tr
                                key={index}
                                className={`border-b last:border-0 hover:bg-muted/50 ${term.taken === false ? 'text-muted-foreground/50' : ''}`}
                              >
                                <td className="px-4 py-2 text-sm">{term.name}</td>
                                <td className="px-4 py-2 text-sm">
                                  <StatusBadge status={{type: 'term', status: term.taken_status as any}} />
                                </td>
                                <td className="px-4 py-2 text-sm">{term.length} months</td>
                                <td className="px-4 py-2 text-sm">{formatDate(term.start_date)}</td>
                                <td className="px-4 py-2 text-sm">{formatDate(term.end_date)}</td>
                                <td className="px-4 py-2 text-sm group relative">
                                  {term.sku ? (
                                    <div className="flex items-center gap-2">
                                      <span>{term.sku}</span>
                                      <CopyButton value={term.sku} label="Copy SKU" />
                                    </div>
                                  ) : (
                                    "N/A"
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="px-4 py-3 text-center text-muted-foreground">
                                No terms available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Updates section */}
                <div>
                  <h2 className="text-lg font-semibold mb-4">Updates</h2>
                  <div className="space-y-4">
                    <UpdateTimeline />
                  </div>
                </div>
              </div>
            </div>

            {/* Right sidebar */}
            <div className="space-y-6 p-6 bg-gray-50/50 border-l border-l-gray-100 p-4 md:col-span-1 min-h-full">
              <VerticalTable
                title="Deal Info"
                rows={[
                  {
                    key: "Team",
                    value: (
                      <Link href={`/teams/${deal.team.id}`} className="hover:underline">
                        {deal.team.name}
                      </Link>
                    )
                  },
                  {
                    key: "Agent",
                    value: deal.agent ? (
                      <Link href={`/agents/${deal.agent.id}`} className="hover:underline">
                        {deal.agent.name}
                      </Link>
                    ) : null
                  },
                  {key: "Event Date", value: formatDate(deal.event_date) },
                  { key: "Salesforce ID", value: deal.salesforce_id }
                ]}
              />

              <VerticalTable
                title="Accounting"
                rows={[
                  {
                    key: "Fee Rate",
                    value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(deal.fee_rate)
                  },
                  { key: "Fee Unit", value: deal.fee_unit },
                  { key: "Fee Cap", value: deal.fee_cap ? `$${deal.fee_cap}` : null },
                  { key: "Reporting Basis", value: deal.reporting_basis }
                ]}
              />
              <NotesDisplay notes={deal.notes} />

              {/* Awards section */}
              <div className="border-t border-t-gray-100 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Awards</h2>
                  <ButtonLink
                    href={`/deals/${deal.id}/awards/new`}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>New Award</span>
                  </ButtonLink>
                </div>
                <div className="space-y-4">
                  {awards && awards.length > 0 ? (
                    <div className="space-y-6">
                      {awards.map((award, index) => (
                        <Card key={award.id} className="border-none">
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <div className="flex items-center space-x-2">
                              <Building2 className="h-5 w-5 text-muted-foreground" />
                              <CardTitle className="text-lg">
                                <Link href={`/suppliers/${award.supplier.id}`} className="hover:underline">
                                  {award.supplier.name}
                                </Link>
                              </CardTitle>
                            </div>
                            <ButtonLink
                              href={`/deals/${deal.id}/awards/${award.id}/edit`}
                              variant="ghost"
                              size="sm"
                            >
                              <Edit className="h-4 w-4" />
                            </ButtonLink>
                          </CardHeader>
                          <CardContent>
                            {award.award_contacts && award.award_contacts.length > 0 ? (
                              <div className="space-y-4">
                                {award.award_contacts.map((contact, i) => (
                                  <div key={contact.id} className="space-y-2">
                                    { i > 0 && (<Separator />) }
                                    <h4 className="font-medium">{contact.first_name} {contact.last_name}</h4>
                                    <div className="space-y-2 text-sm">
                                      {contact.email && (
                                        <div className="flex items-center space-x-2 group">
                                          <Mail className="h-4 w-4 text-muted-foreground" />
                                          <a href={`mailto:${contact.email}`} className="hover:underline">
                                            {contact.email}
                                          </a>
                                          <CopyButton value={contact.email} label="Copy Email" />
                                        </div>
                                      )}
                                      {contact.phone_office && (
                                        <div className="flex items-center space-x-2">
                                          <Phone className="h-4 w-4 text-muted-foreground" />
                                          <span>
                                            {formatPhoneNumber(contact.phone_office)}
                                            <span className="text-muted-foreground ml-1">(office)</span>
                                          </span>
                                        </div>
                                      )}
                                      {contact.phone_mobile && (
                                        <div className="flex items-center space-x-2">
                                          <Phone className="h-4 w-4 text-muted-foreground" />
                                          <span>
                                            {formatPhoneNumber(contact.phone_mobile)}
                                            <span className="text-muted-foreground ml-1">(mobile)</span>
                                          </span>
                                        </div>
                                      )}
                                      {contact.phone_other && (
                                        <div className="flex items-center space-x-2">
                                          <Phone className="h-4 w-4 text-muted-foreground" />
                                          <span>
                                            {formatPhoneNumber(contact.phone_other)}
                                            <span className="text-muted-foreground ml-1">(other)</span>
                                          </span>
                                        </div>
                                      )}
                                      {contact.address_1 && (
                                        <div className="flex items-center space-x-2">
                                          <MapPin className="h-4 w-4 text-muted-foreground" />
                                          <span>
                                            {contact.address_1}
                                            {contact.address_2 && `, ${contact.address_2}`}
                                            {contact.city && `, ${contact.city}`}
                                            {contact.state && `, ${contact.state}`}
                                            {contact.postal_code && ` ${contact.postal_code}`}
                                          </span>
                                        </div>
                                      )}
                                      {contact.notes && (
                                        <div className="flex items-start space-x-2 mt-2">
                                          <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                                          <p className="text-sm text-muted-foreground whitespace-pre-line">
                                            {contact.notes}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">No contacts available</p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No awards available</p>
                  )}
                </div>
              </div>

              {/* Team Members */}
              <div className="border-t border-t-gray-100 pt-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-muted-foreground" />
                  Portfolio Users
                </h2>
                <div className="space-y-4">
                  {team_members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-2 border rounded-lg">
                      <div>
                        <Link href={`/users/${member.id}`} className="font-medium hover:underline">{member.name}</Link>
                        <p className="text-sm text-gray-500">{member.email}</p>
                      </div>
                    </div>
                  ))}

                  {team_members.length === 0 && (
                    <p className="text-sm text-muted-foreground">Team has no portfolio users</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
